function esc(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

const CLP = new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
});

const QTY = new Intl.NumberFormat('es-CL', {
    maximumFractionDigits: 3,
});

function formatQty(value, ventaPorPeso) {
    const n = Number(value) || 0;
    return `${QTY.format(n)}${ventaPorPeso ? ' kg' : ''}`;
}

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return esc(value);
    return d.toLocaleDateString('es-CL', { timeZone: 'America/Santiago' });
}

function lineHtml(linea) {
    const precio = Number(linea.precio_unitario) || 0;
    const cantidad = Number(linea.cantidad_pedida) || 0;
    const total = linea.total_linea != null
        ? Number(linea.total_linea)
        : cantidad * precio;
    return `<tr>
        <td>${esc(linea.codigo_proveedor || '—')}</td>
        <td>${esc(linea.codigo_barra || '—')}</td>
        <td>${esc(linea.producto_nombre)}</td>
        <td class="num">${esc(formatQty(cantidad, linea.venta_por_peso))}</td>
        <td class="num">${CLP.format(Math.round(precio))}</td>
        <td class="num">${CLP.format(Math.round(total))}</td>
    </tr>`;
}

function hojaHtml(solicitud) {
    const lineas = Array.isArray(solicitud.lineas) ? solicitud.lineas : [];
    const total = lineas.reduce((acc, linea) => {
        const cantidad = Number(linea.cantidad_pedida) || 0;
        const precio = Number(linea.precio_unitario) || 0;
        return acc + (linea.total_linea != null ? Number(linea.total_linea) : cantidad * precio);
    }, 0);

    const fecha = formatDate(solicitud.fecha_solicitud || solicitud.created_at);
    const estado = solicitud.estado ? String(solicitud.estado).toUpperCase() : '';

    return `
    <section class="hoja">
        <header class="header">
            <div>
                <div class="brand">Super Colchagua</div>
                <div class="muted">Solicitud de compra</div>
            </div>
            <div class="doc">
                <h1>${esc(solicitud.numero || `SC-${solicitud.id}`)}</h1>
                <div class="muted">${esc(fecha)}</div>
                ${estado ? `<div class="badge">${esc(estado)}</div>` : ''}
            </div>
        </header>

        <div class="proveedor">
            <div class="muted">Proveedor</div>
            <strong>${esc(solicitud.proveedor_nombre || '—')}</strong>
            <div>${esc(solicitud.proveedor_direccion || '')}</div>
            <div>
                ${solicitud.proveedor_telefono ? `Tel. ${esc(solicitud.proveedor_telefono)}` : ''}
                ${solicitud.proveedor_telefono && solicitud.proveedor_email ? ' · ' : ''}
                ${solicitud.proveedor_email ? esc(solicitud.proveedor_email) : ''}
            </div>
        </div>

        <table>
            <thead>
                <tr>
                    <th>Código proveedor</th>
                    <th>Código barra</th>
                    <th>Producto</th>
                    <th class="num">Cantidad</th>
                    <th class="num">Precio</th>
                    <th class="num">Total</th>
                </tr>
            </thead>
            <tbody>
                ${lineas.map(lineHtml).join('') || '<tr><td colspan="6" class="empty">Sin líneas</td></tr>'}
            </tbody>
            <tfoot>
                <tr class="totals">
                    <td colspan="5">Total estimado</td>
                    <td class="num">${CLP.format(Math.round(total))}</td>
                </tr>
            </tfoot>
        </table>

        ${solicitud.observaciones ? `<div class="obs"><span class="muted">Observaciones:</span> ${esc(solicitud.observaciones)}</div>` : ''}

        <footer class="foot">
            Documento interno del ERP. Los precios son cotizados y pueden variar al recibir la factura.
            ${solicitud.usuario_nombre ? ` Armado por ${esc(solicitud.usuario_nombre)}.` : ''}
        </footer>
    </section>`;
}

/**
 * Imprime una hoja por solicitud de compra, sin mostrar ofertas de otros
 * proveedores. Devuelve false si el navegador bloquea la ventana emergente.
 *
 * @param {object|object[]} solicitud solicitud con `lineas`
 */
export function printSolicitudesCompra(solicitud) {
    const lista = Array.isArray(solicitud) ? solicitud : [solicitud];
    const validas = lista.filter(Boolean);
    if (validas.length === 0) return false;

    const printWindow = window.open('', '_blank', 'width=900,height=1100');
    if (!printWindow) return false;

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Solicitud de compra ${esc(validas.map((s) => s.numero || s.id).join(', '))}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1c1917; font-size: 12px; margin: 0; }
  .hoja { page-break-after: always; }
  .hoja:last-child { page-break-after: auto; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1c1917; padding-bottom: 10px; margin-bottom: 14px; }
  .brand { font-weight: 700; font-size: 18px; letter-spacing: 0.02em; }
  .doc { text-align: right; }
  .doc h1 { font-size: 18px; margin: 0 0 2px; letter-spacing: 0.04em; }
  .badge { display: inline-block; margin-top: 4px; border: 1px solid #1c1917; border-radius: 999px; padding: 1px 8px; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; }
  .muted { color: #57534e; }
  .proveedor { border: 1px solid #d6d3d1; border-radius: 6px; padding: 8px 10px; margin-bottom: 14px; line-height: 1.4; }
  .proveedor strong { font-size: 13px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 6px 8px; border-bottom: 1px solid #e7e5e4; vertical-align: top; }
  th { background: #f5f5f4; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; }
  td.num, th.num { text-align: right; white-space: nowrap; }
  tfoot .totals td { font-weight: 700; border-top: 2px solid #1c1917; border-bottom: none; font-size: 13px; }
  .empty { text-align: center; color: #78716c; padding: 18px; }
  .obs { margin-top: 12px; border-left: 3px solid #d6d3d1; padding: 6px 10px; }
  .foot { margin-top: 26px; font-size: 10px; color: #78716c; border-top: 1px solid #e7e5e4; padding-top: 8px; }
</style></head><body>
  ${validas.map(hojaHtml).join('')}
  <script>window.onload=function(){window.print();}</script>
</body></html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    return true;
}

export function printSolicitudCompra(solicitud) {
    return printSolicitudesCompra(solicitud);
}
